import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import styled from "styled-components";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogContentText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import { toast } from "react-toastify";
import { ActionButtonConfirmStatus } from "components";
import { api } from "../../../../../api/api";
import { useTypedSelector } from "store";
import {
  ADD_NEW_TABLE,
  SET_TABLE,
} from "store/reducers/products/tables/tables.actions";
import { ProcessState } from "types/process";
import { ImportTsvAction, ImportTsvMatch } from "types/misc";
import { parseTsvTableIdentity } from "./parseTsvTableIdentity";
import { ImportTSVReplaceTablePreview } from "./ImportTSVReplaceTablePreview";

const MAX_TSV_BYTES = 50 * 1024 * 1024;

export default function ImportTableFromTSVButton() {
  const dispatch = useDispatch();
  const analyzeGeneration = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [importMatch, setImportMatch] = useState<ImportTsvMatch | null>(null);
  const [importAction, setImportAction] = useState<ImportTsvAction>(
    ImportTsvAction.Create
  );
  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [statusMessage, setStatusMessage] = useState<ReactNode>("");

  const { selectedProductLine: productLine, tables } = useTypedSelector(
    (state) => state.products
  );

  const importDisabled = !productLine?.id;
  const isReplaceCandidate = Boolean(importMatch?.isReplaceCandidate);
  const effectiveImportAction = isReplaceCandidate
    ? importAction
    : ImportTsvAction.Create;

  const resetAnalyzeState = useCallback(() => {
    setAnalyzing(false);
    setAnalyzeError(null);
    setImportMatch(null);
    setImportAction(ImportTsvAction.Create);
  }, []);

  const runImport = useCallback(
    async (file: File, action: ImportTsvAction) => {
      if (!productLine?.id) {
        setProcessState(ProcessState.ERROR);
        setStatusMessage("No product line selected.");
        return;
      }

      try {
        setProcessState(ProcessState.PROCESSING);
        setStatusMessage("Importing table from TSV...");

        const { data } = await api.importRuleTableFromTSV(
          productLine.id,
          file,
          action
        );

        if (action === ImportTsvAction.Replace) {
          dispatch({
            type: SET_TABLE,
            payload: { data, tableId: data.id },
          });
        } else {
          dispatch({
            type: ADD_NEW_TABLE,
            payload: {
              byId: { [data.id]: data, ...tables.byId },
              allIds: tables.allIds.concat(data.id),
              total: tables.total,
            },
          });
        }

        const msg =
          action === ImportTsvAction.Replace
            ? "Table replaced from TSV."
            : "New table imported from TSV.";
        toast.success(msg);
        setProcessState(ProcessState.COMPLETE);
        setStatusMessage(msg);
      } catch (e: unknown) {
        let message: string;
        if (axios.isAxiosError(e) && e.response?.data) {
          message =
            typeof e.response.data === "string"
              ? e.response.data
              : String(e.response.data);
        } else if (e instanceof Error) {
          message = e.message;
        } else {
          message = String(e);
        }

        const lines = message
          .split(/[\r\n]+/)
          .filter((line) => line.length > 0);
        toast.error(lines[0] ?? "Error importing table.");
        setProcessState(ProcessState.ERROR);
        setStatusMessage(
          <Box sx={{ whiteSpace: "pre-wrap" }}>
            {lines.map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </Box>
        );
        console.error("Error importing table:", e);
      }
    },
    [dispatch, productLine?.id, tables]
  );

  const analyzeFile = useCallback(
    async (file: File) => {
      if (!productLine?.id) return;

      const generation = ++analyzeGeneration.current;
      setAnalyzing(true);
      setAnalyzeError(null);
      setImportMatch(null);
      setImportAction(ImportTsvAction.Create);

      try {
        const text = await file.text();
        if (generation !== analyzeGeneration.current) return;

        const identity = parseTsvTableIdentity(text);
        if (!identity.id && !identity.compareId) {
          setImportMatch({ isReplaceCandidate: false });
          await runImport(file, ImportTsvAction.Create);
          return;
        }

        const response = await api.getImportTsvMatch(
          productLine.id,
          identity.id,
          identity.compareId
        );
        if (generation !== analyzeGeneration.current) return;

        if (response.status >= 400 || !response.data) {
          setAnalyzeError(
            "Could not check for an existing table. Import will create a new table."
          );
          setImportMatch({ isReplaceCandidate: false });
          return;
        }

        setImportMatch(response.data);
        if (!response.data.isReplaceCandidate) {
          await runImport(file, ImportTsvAction.Create);
        }
      } catch (e) {
        if (generation !== analyzeGeneration.current) return;
        console.error("Error checking TSV import match:", e);
        setAnalyzeError(
          "Could not check for an existing table. Import will create a new table."
        );
        setImportMatch({ isReplaceCandidate: false });
      } finally {
        if (generation === analyzeGeneration.current) {
          setAnalyzing(false);
        }
      }
    },
    [productLine?.id, runImport]
  );

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0] ?? null;
    setFileError(null);
    setSelectedFile(file);
    e.target.value = "";
    resetAnalyzeState();

    if (!file) return;

    if (file.size > MAX_TSV_BYTES) {
      setFileError("The selected file exceeds the 50 MB limit.");
      setSelectedFile(null);
      return;
    }

    const name = file.name.toLowerCase();
    const validExt = name.endsWith(".tsv");
    const validMime =
      file.type === "text/tab-separated-values" ||
      file.type === "text/tsv" ||
      file.type === "text/plain" ||
      file.type === "";

    if (!validExt && !validMime) {
      setFileError("Please choose a valid TSV file (.tsv)");
      setSelectedFile(null);
      return;
    }

    void analyzeFile(file);
  };

  const onDialogOpen = useCallback(() => {
    analyzeGeneration.current += 1;
    setSelectedFile(null);
    setFileError(null);
    resetAnalyzeState();
    setProcessState(ProcessState.IDLE);
    setStatusMessage("");
    // Same user-click as opening the dialog, so the browser allows the picker.
    fileInputRef.current?.click();
  }, [resetAnalyzeState]);

  const handleImport = useCallback(async () => {
    if (!selectedFile) {
      setProcessState(ProcessState.ERROR);
      setStatusMessage("Select a TSV file before importing.");
      return;
    }

    await runImport(selectedFile, effectiveImportAction);
  }, [effectiveImportAction, runImport, selectedFile]);

  const contentSection = useMemo<ReactNode>(() => {
    const fileLabel = selectedFile
      ? `Selected: ${selectedFile.name}`
      : "Choose TSV (.tsv)…";

    return (
      <ContentStack>
        <DialogContentText>
          Select a TSV file. If it matches an existing table, choose whether to
          replace that table or create a new one. Otherwise it is imported as a
          new table automatically.
        </DialogContentText>

        <Box>
          <Button
            variant="outlined"
            startIcon={<BackupOutlinedIcon />}
            sx={{ textTransform: "none" }}
            onClick={() => fileInputRef.current?.click()}
          >
            {fileLabel}
          </Button>

          {fileError && (
            <Box mt={1}>
              <Alert severity="error">{fileError}</Alert>
            </Box>
          )}
        </Box>

        {analyzing && processState === ProcessState.IDLE && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Checking for a matching table…
            </Typography>
          </Box>
        )}

        {analyzeError && !analyzing && processState === ProcessState.IDLE && (
          <Alert severity="warning">{analyzeError}</Alert>
        )}

        {isReplaceCandidate &&
          !analyzing &&
          processState === ProcessState.IDLE && (
            <Box sx={{ minWidth: 260 }}>
              <Typography variant="body1" sx={{ ml: 0.5 }}>
                Matching table found by {importMatch?.matchBy}:
              </Typography>
              <Select
                fullWidth
                size="small"
                value={importAction}
                onChange={(e) =>
                  setImportAction(e.target.value as ImportTsvAction)
                }
              >
                <MenuItem value={ImportTsvAction.Create}>
                  <strong>Create</strong>: Import as a new table
                </MenuItem>
                <MenuItem value={ImportTsvAction.Replace}>
                  <strong>Replace</strong>: Overwrite the matching{" "}
                  {importMatch?.tableType} table
                </MenuItem>
              </Select>
              {importMatch?.tableId && (
                <Box mt={1.5}>
                  <ImportTSVReplaceTablePreview tableId={importMatch.tableId} />
                </Box>
              )}
            </Box>
          )}
      </ContentStack>
    );
  }, [
    analyzeError,
    analyzing,
    fileError,
    importAction,
    importMatch?.matchBy,
    importMatch?.tableId,
    importMatch?.tableType,
    isReplaceCandidate,
    processState,
    selectedFile,
  ]);

  return (
    <Wrapper>
      <input
        ref={fileInputRef}
        type="file"
        accept=".tsv,text/tab-separated-values,text/plain"
        hidden
        onChange={onFileChange}
      />
      <ActionButtonConfirmStatus
        variant="outlined"
        color="primary"
        size="small"
        disabled={importDisabled}
        icon={BackupOutlinedIcon}
        iconOnly={false}
        tooltipText="Import table from TSV"
        tooltipArrow
        tooltipPlacement="bottom"
        maxWidth={isReplaceCandidate ? "xl" : "sm"}
        fullWidth
        dialogTitle="Import Table from TSV"
        onDialogOpen={onDialogOpen}
        disableEscapeKeyDown
        disableCloseOnBackdropClick
        contentSection={contentSection}
        hideContentOnProcessComplete
        defaultButton="OK"
        cancelButtonText="Cancel"
        okButtonText={
          effectiveImportAction === ImportTsvAction.Replace
            ? "Import and replace existing table"
            : "Import as new"
        }
        okButtonDisabled={!selectedFile || !!fileError || analyzing}
        okButtonWarningColor={effectiveImportAction === ImportTsvAction.Replace}
        onOKButtonClick={handleImport}
        processState={processState}
        statusMessage={statusMessage}
      >
        Import from TSV
      </ActionButtonConfirmStatus>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: 10px;
`;

const ContentStack = styled(Stack)`
  gap: 12px;
`;
